<template>
  <form @submit.prevent="submitCode">
    <FormGroup>
      <View direction="column" gap="sm">
        <Text variant="h1">Two-step verification</Text>
        <Text variant="body-large" color="secondary">Enter the 6-digit code from your authenticator app.</Text>
      </View>

      <FormField label="Verification code" :error="form.fieldErrors('code')">
        <TwoFactorCodeField ref="codeFieldRef" v-model="form.fields.code" @complete="submitCode" />
      </FormField>

      <Button type="submit" :loading="form.submitting">Verify</Button>

      <Text as="div" variant="body-large" align="center">
        <Link :to="backToSignInRoute" color="accent">Back to sign in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { onMounted, ref, watch } from 'vue';
import { RouteLocationRaw, useRouter } from 'vue-router';

import { Button, extractErrorMessage, FormField, FormGroup, Link, Text, View, useForm } from '@tallo/design-system';

import { useAuthStore } from '../auth.store';
import TwoFactorCodeField from './two-factor-code-field.vue';

const props = defineProps<{
  afterLoginRoute: RouteLocationRaw;
  backToSignInRoute: RouteLocationRaw;
}>();

const router = useRouter();
const authStore = useAuthStore();

const form = useForm(
  { code: '' },
  {
    code: {
      required: helpers.withMessage('Enter the code', required),
    },
  },
);

const codeFieldRef = ref<InstanceType<typeof TwoFactorCodeField>>();

// The challenge lives only in memory - after a refresh there is nothing to verify against
onMounted(() => {
  if (!authStore.isTwoFactorPending) {
    router.replace(props.backToSignInRoute);
    return;
  }

  codeFieldRef.value?.focus();
});

watch(
  () => form.fields.code,
  () => form.clearValidationErrors(),
);

function submitCode() {
  form.submit(async () => {
    try {
      await authStore.signInWithTwoFactorCode(form.fields.code.trim());
      // Resume wherever the sign-in was originally headed (a deep link), else the default
      const redirect = authStore.consumePostSignInRedirect();
      await router.push(redirect ?? props.afterLoginRoute);
    } catch (error) {
      form.setFieldError('code', extractErrorMessage(error, 'Invalid code. Please try again.'));
    }
  });
}
</script>
